// PR flow - pull requests in against pull requests out.
//
// Deliberately NOT a panel on campaign health (ADR-0028). Campaign health is a
// ticket and sprint instrument, and every number on it is commitment-scoped:
// "behind" there means behind a promise. Pull requests have no promise, so
// "behind" here can only mean BACKLOG: how many are open. Different noun. Two
// panels on one page would invite the reader to assume they measure the same
// thing.
//
// Admin-only, like the Commander view.

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { isWeekend, silentWeekCount, weekdayLabel, weekdayLetter, type PrFlowWeek } from './pr-flow';

interface PrFlowResponse {
  weeks: PrFlowWeek[];
  totals: { opened: number; closed: number };
  backlog_change: number;
  granularity: 'day' | 'week';
  window_count: number;
  scope_note: string;
}

// Days for the short windows, weeks for the long ones. Per-day is what an
// operator actually wants - 12 weeks smooths a bad fortnight into nothing - but
// a year of days is 364 columns at two pixels each, which is a smear rather than
// a chart. Day is the default; week is there for the long view.
const DAY_WINDOWS = [7, 14, 30, 60, 90];
const WEEK_WINDOWS = [2, 4, 8, 12, 26, 52];

// Local shape for the team picker. lib/types does not export a Team.
interface TeamLite { id: string; name: string }

export function PrFlowPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useQuery<{ teams: TeamLite[] }>({ queryKey: ['teams'], queryFn: () => api<{ teams: TeamLite[] }>('/api/teams') });
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';
  const granularity: 'day' | 'week' = searchParams.get('granularity') === 'week' ? 'week' : 'day';
  const windowOptions = granularity === 'day' ? DAY_WINDOWS : WEEK_WINDOWS;
  const count = Number(searchParams.get('count') ?? (granularity === 'day' ? 14 : 12));

  const flow = useQuery<PrFlowResponse>({
    queryKey: ['pr-flow', teamId, granularity, count],
    queryFn: () =>
      api<PrFlowResponse>(
        `/api/admin/pr-flow?team_id=${encodeURIComponent(teamId)}&granularity=${granularity}&count=${count}`,
      ),
    enabled: !!teamId,
  });

  const data = flow.data;
  const series = data?.weeks ?? [];
  const max = Math.max(1, ...series.map((w) => Math.max(w.opened, w.closed)));
  const silent = silentWeekCount(series);
  // In day mode the column is a real weekday, and naming it is the difference
  // between "bars" and "the week". Two letters while they fit, one letter after
  // that: at 30 days and up two letters collide, but a single letter still fits
  // in a column and is enough to read the weekly rhythm at a glance.
  const showDayLabels = granularity === 'day';
  const twoLetterLabels = count <= 14;


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">PR flow</h1>
          <p className="text-xs text-zinc-400">
            Pull requests opened against closed, and the backlog between them.
            Not campaign-scoped: a PR carries no commitment, so &quot;behind&quot; here means backlog size.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
            value={teamId}
            onChange={(e) => setSearchParams({ team_id: e.target.value, granularity, count: String(count) })}
          >
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select
            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
            value={granularity}
            onChange={(e) =>
              setSearchParams({
                team_id: teamId,
                granularity: e.target.value,
                count: String(e.target.value === 'day' ? 14 : 12),
              })
            }
          >
            <option value="day">Per day</option>
            <option value="week">Per week</option>
          </select>
          <select
            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
            value={String(count)}
            onChange={(e) => setSearchParams({ team_id: teamId, granularity, count: e.target.value })}
          >
            {windowOptions.map((w) => (
              <option key={w} value={w}>Last {w} {granularity === 'day' ? (w === 1 ? 'day' : 'days') : 'weeks'}</option>
            ))}
          </select>
        </div>
      </div>

      {flow.isError ? (
        <div className="card"><p className="text-sm text-red-300">Could not load PR flow.</p></div>
      ) : null}

      {data ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Opened</p>
              <p className="text-2xl font-semibold text-sky-300">{data.totals.opened}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Closed</p>
              <p className="text-2xl font-semibold text-emerald-300">{data.totals.closed}</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-zinc-400">Backlog change</p>
              <p className={`text-2xl font-semibold ${data.backlog_change > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
                {data.backlog_change > 0 ? '+' : ''}{data.backlog_change}
              </p>
              <p className="text-xs text-zinc-500">
                {data.backlog_change > 0 ? 'the pile grew' : 'the pile shrank'}
              </p>
            </div>
          </div>

          <div className="card">
            <h2 className="text-sm font-medium text-zinc-200">Opened vs closed, by {granularity}</h2>
            <p className="mb-3 text-xs text-zinc-500">
              <span className="text-sky-300">Opened above the line</span>,{' '}
              <span className="text-emerald-300">closed below it</span>. A week that opens more than it
              closes pushes out of the middle; a week that closes more pulls back in. The bars are the
              {granularity === 'day' ? ' day' : ' week'}; the Backlog change figure above is where they have netted out.
              {granularity === 'day' ? ' Shaded columns are weekends.' : ''}
              {silent > 0 ? ` ${silent} week${silent === 1 ? '' : 's'} recorded no PR events at all; the line is broken across ${silent === 1 ? 'it' : 'them'} rather than drawn through.` : ''}
            </p>

            {/* Diverging, not side by side: OPENED above the line, CLOSED below.
                The line is the thing being asked about - the backlog sits on it,
                and a week that opens more than it closes visibly pushes up out of
                it. Two bars standing next to each other answer "how much" but not
                "which way", and which way is the whole question.
                Each column is two flex halves so neither bar can escape its own
                side, and the silent placeholder spans both. */}
            <div className="flex items-stretch gap-1">
              {series.map((w) => {
                const wknd = granularity === 'day' && isWeekend(w.bucket_start);
                return (
                <div
                  key={w.bucket_start}
                  className={wknd ? 'flex flex-1 flex-col rounded-t-sm bg-white/[0.04]' : 'flex flex-1 flex-col'}
                  style={{ height: 132 }}
                  title={`${w.bucket_start}${wknd ? ' (weekend)' : ''} · ${w.opened} opened, ${w.closed} closed`}
                >
                  {w.silent ? (
                    <div className="my-0.5 flex-1 rounded-sm border border-dashed border-zinc-700" />
                  ) : (
                    <>
                      <div className="flex flex-1 items-end">
                        <div
                          className="w-full rounded-t-sm bg-sky-400/70"
                          style={{ height: Math.max(2, (w.opened / max) * 58) }}
                        />
                      </div>
                      <div className="h-px w-full bg-white/15" />
                      <div className="flex flex-1 items-start">
                        <div
                          className="w-full rounded-b-sm bg-emerald-400/70"
                          style={{ height: Math.max(2, (w.closed / max) * 58) }}
                        />
                      </div>
                    </>
                  )}
                </div>
                );
              })}
            </div>
            {showDayLabels ? (
              <div className="mt-1 flex gap-1">
                {series.map((w) => (
                  <div
                    key={w.bucket_start}
                    className={
                      isWeekend(w.bucket_start)
                        ? 'flex-1 text-center text-[10px] text-zinc-600'
                        : 'flex-1 text-center text-[10px] text-zinc-500'
                    }
                  >
                    {twoLetterLabels ? weekdayLabel(w.bucket_start) : weekdayLetter(w.bucket_start)}
                  </div>
                ))}
              </div>
            ) : null}

            <p className="mt-1 text-xs text-zinc-500">
              {series[0]?.bucket_start} → {series[series.length - 1]?.bucket_start}
            </p>
          </div>

          <p className="text-xs text-zinc-500">{data.scope_note}</p>
        </>
      ) : flow.isLoading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : null}
    </div>
  );
}
